import type Phaser from 'phaser';

/**
 * Texture keys for the coin's pixel-flip spin, in play order. 'coin' is the full
 * face (also the static icon); the rest are progressively narrower crisp frames
 * down to a side-on edge, then back out — a classic sprite-coin spin. All frames
 * are generated (NEAREST-filtered) in makePlaceholderTextures().
 */
export const COIN_SPIN_FRAMES = [
  'coin',
  'coin-spin-2',
  'coin-spin-3',
  'coin-spin-edge',
  'coin-spin-3',
  'coin-spin-2',
] as const;

/** The Capycoin's spin (the Capybara Event) — the same derivation off 'capycoin'. */
export const CAPYCOIN_SPIN_FRAMES = [
  'capycoin',
  'capycoin-spin-2',
  'capycoin-spin-3',
  'capycoin-spin-edge',
  'capycoin-spin-3',
  'capycoin-spin-2',
] as const;

/** The Dark Hambi Coin's spin (the Origin Event) — off 'darkcoin'. */
export const DARKCOIN_SPIN_FRAMES = [
  'darkcoin',
  'darkcoin-spin-2',
  'darkcoin-spin-3',
  'darkcoin-spin-edge',
  'darkcoin-spin-3',
  'darkcoin-spin-2',
] as const;

/** The Light Hambi Coin's spin (Above the Clouds) — off 'lightcoin'. */
export const LIGHTCOIN_SPIN_FRAMES = [
  'lightcoin',
  'lightcoin-spin-2',
  'lightcoin-spin-3',
  'lightcoin-spin-edge',
  'lightcoin-spin-3',
  'lightcoin-spin-2',
] as const;

/** Per-frame hold (ms). Six frames → ~900 ms per full revolution. */
const FRAME_MS = 150;

/**
 * Cycle an Image through texture keys on a fixed timer — crisp frame-based
 * motion (the coin flip, the capybara run) with no smooth sub-pixel squash.
 * Call once per image after adding it. Self-cleaning: the timer removes
 * itself once the target is destroyed.
 */
export function cycleFrames(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Image,
  frames: readonly string[],
  ms: number,
): void {
  let i = 0;
  const ev = scene.time.addEvent({
    delay: ms,
    loop: true,
    callback: () => {
      if (!target.active) {
        ev.remove();
        return;
      }
      i = (i + 1) % frames.length;
      target.setTexture(frames[i]);
    },
  });
}

/**
 * Spin a coin Image by cycling its pixel-flip frames (the Hambi coin's by
 * default; pass CAPYCOIN_SPIN_FRAMES for a Capycoin).
 */
export function spinCoin(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Image,
  frames: readonly string[] = COIN_SPIN_FRAMES,
): void {
  cycleFrames(scene, target, frames, FRAME_MS);
}
